import { Component, type ReactNode } from "react";
import { Link } from "react-router-dom";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-6">
          <div className="max-w-lg text-center">
            <span className="font-mono text-xs font-bold uppercase tracking-widest text-primary block mb-6">
              [ SYSTEM ERROR ]
            </span>
            <h1 className="text-display-md font-black uppercase mb-4">
              SOMETHING BROKE.
            </h1>
            <p className="text-muted-foreground mb-8">
              An unexpected error occurred. Try refreshing the page.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="font-mono text-xs font-bold uppercase tracking-wider px-5 py-2.5 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                RELOAD
              </button>
              <Link
                to="/"
                className="font-mono text-xs font-bold uppercase tracking-wider px-5 py-2.5 border border-border text-foreground hover:bg-secondary transition-colors"
              >
                GO HOME
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;